/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://statefulcoderz.web.app/product/material-dashboard-react
* Copyright 2025 Stateful Coders (https://https://statefulcoderz.web.app)

Coded by https://statefulcoderz.web.app

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Dashboard components
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardCounts } from "data/controllers/dashboardCountsData";
import { getChartData } from "data/controllers/dashboardCountsData";

function Dashboard() {
  const navigate = useNavigate();
  const [dashboardCountsData, setDashboardCountsData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);

  useEffect(() => {
    async function fetchDashboardCounts() {
      const data = await getDashboardCounts();
      setDashboardCountsData(data);
      const { topLocations, lineData } = await getChartData();
      setChartData(topLocations);
      setLineChartData(lineData);
    }

    fetchDashboardCounts();
  }, []);

  // Navigation handlers for dashboard cards
  const handleUsersClick = () => {
    navigate("/users");
  };

  const handleRecruitersClick = () => {
    navigate("/users?role=recruiter");
  };

  const handleCandidatesClick = () => {
    navigate("/users?role=candidate");
  };

  const handleJobsClick = () => {
    navigate("/jobs");
  };

  const handlePendingJobsClick = () => {
    navigate("/jobs?status=pending");
  };

  const handleActiveJobsClick = () => {
    navigate("/jobs?status=published");
  };

  const handleApplicationsClick = () => {
    navigate("/applications");
  };

  const handleRevenueClick = () => {
    navigate("/purchase-history");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="info"
                icon="people"
                title="Users"
                count={dashboardCountsData?.users?.total || 0}
                percentage={{
                  color: dashboardCountsData?.users?.color || "text",
                  amount: `${dashboardCountsData?.users?.delta || 0}%`,
                  label: dashboardCountsData?.users?.label || "Loading...",
                }}
                onClick={handleUsersClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="info"
                icon="person"
                title="Recruiters"
                count={dashboardCountsData?.recruiters?.total || 0}
                percentage={{
                  color: dashboardCountsData?.recruiters?.color || "text",
                  amount: `${dashboardCountsData?.recruiters?.delta || 0}%`,
                  label: dashboardCountsData?.recruiters?.label || "Loading...",
                }}
                onClick={handleRecruitersClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="info"
                icon="person"
                title="Candidates"
                count={dashboardCountsData?.candidates?.total || 0}
                percentage={{
                  color: dashboardCountsData?.candidates?.color || "text",
                  amount: `${dashboardCountsData?.candidates?.delta || 0}%`,
                  label: dashboardCountsData?.candidates?.label || "Loading...",
                }}
                onClick={handleCandidatesClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="info"
                icon="leaderboard"
                title="Today's Users"
                count={dashboardCountsData?.todaysUsers?.total || 0}
                percentage={{
                  color: dashboardCountsData?.todaysUsers?.color || "text",
                  amount: `${dashboardCountsData?.todaysUsers?.delta || 0}%`,
                  label: dashboardCountsData?.todaysUsers?.label || "Loading...",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="warning"
                icon="work"
                title="Jobs"
                count={dashboardCountsData?.jobs?.total || 0}
                percentage={{
                  color: dashboardCountsData?.jobs?.color || "text",
                  amount: "",
                  label: dashboardCountsData?.jobs?.label || "Loading...",
                }}
                onClick={handleJobsClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="warning"
                icon="work_outline"
                title="Pending Jobs"
                count={dashboardCountsData?.jobs?.pending || 0}
                percentage={{
                  color: dashboardCountsData?.jobs?.color || "text",
                  amount: "",
                  label: dashboardCountsData?.jobs?.label || "Loading...",
                }}
                onClick={handlePendingJobsClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="warning"
                icon="work_outline"
                title="Active Jobs"
                count={dashboardCountsData?.jobs?.active || 0}
                percentage={{
                  color: dashboardCountsData?.jobs?.color || "text",
                  amount: "",
                  label: dashboardCountsData?.jobs?.label || "Loading...",
                }}
                onClick={handleActiveJobsClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="dark"
                icon="assignment"
                title="Applications"
                count={dashboardCountsData?.applications?.total || 0}
                percentage={{
                  color: dashboardCountsData?.applications?.color || "text",
                  amount: "",
                  label: dashboardCountsData?.applications?.label || "Loading...",
                }}
                onClick={handleApplicationsClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="success"
                icon="store"
                title="Revenue"
                count={`₹${dashboardCountsData?.revenue?.total?.total || 0}`}
                percentage={{
                  color: dashboardCountsData?.revenue?.total?.color || "text",
                  // amount: `+${dashboardCountsData?.revenue?.total?.delta}%`,
                  label: dashboardCountsData?.revenue?.total?.label || "Loading...",
                }}
                onClick={handleRevenueClick}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox mb={{ xs: 1, md: 1.5 }}>
              <ComplexStatisticsCard
                color="success"
                icon="store"
                title="Today's Revenue"
                count={`₹${dashboardCountsData?.revenue?.today?.total || 0}`}
                percentage={{
                  color: dashboardCountsData?.revenue?.today?.color || "text",
                  amount: `${dashboardCountsData?.revenue?.today?.delta || 0}%`,
                  label: dashboardCountsData?.revenue?.today?.label || "Loading...",
                }}
                onClick={handleRevenueClick}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={{ xs: 3, md: 4.5 }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={{ xs: 2, md: 3 }}>
                <ReportsBarChart
                  color="info"
                  title="Locations"
                  description="Top 5 Cities"
                  date="Just updated"
                  chart={chartData || {}}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={{ xs: 2, md: 3 }}>
                <ReportsLineChart
                  color="success"
                  title="Users"
                  description="Last 7 Days Data"
                  date="Just updated"
                  chart={lineChartData?.users || {}}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={{ xs: 2, md: 3 }}>
                <ReportsLineChart
                  color="dark"
                  title="Applications"
                  description="Last 7 Days Data"
                  date="just updated"
                  chart={lineChartData?.application || {}}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
